import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../AuthContext';
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import "../../assets/CSS/Dashboard.css";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const IMAGE_BASE_URL = process.env.REACT_APP_API_BASE_PATH || BASE_URL;
const normalizeRole = (role) => String(role || '').replace(/[\s_-]+/g, '').toLowerCase();

export default function ProductionReport() {
  const { user } = useContext(AuthContext);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSidebarVisible, setIsSidebarVisible] = useState(window.innerWidth >= 768);

  // Filters State
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [workerFilter, setWorkerFilter] = useState("");
  const [supervisorFilter, setSupervisorFilter] = useState("");

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    if (!user) {
      window.location.href = "/";
      return;
    }
    fetchReports();
  }, [user]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/wip-dashboard/admin/production-report`, {
        params: {
          role: user?.Auth,
          department: user?.Department,
          // Super Admin must always get the unfiltered factory view, including
          // users whose stored role is written as "Super Admin".
          scope: normalizeRole(user?.Auth) === 'superadmin' ? 'all' : undefined
        },
        withCredentials: true
      });
      if (res.data.success) {
        setReports(res.data.reports);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch production report', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Derive unique workers for dropdown
  const uniqueWorkers = useMemo(() => {
    const workers = new Map();
    reports.forEach(r => {
      if (r.worker_id && !workers.has(r.worker_id)) {
        workers.set(r.worker_id, `${r.First_Name} ${r.Last_Name}`.trim());
      }
    });
    return Array.from(workers.entries()).map(([id, name]) => ({ id, name }));
  }, [reports]);

  // Derive unique supervisors for dropdown
  const uniqueSupervisors = useMemo(() => {
    const supervisors = new Set();
    reports.forEach(r => {
      if (r.supervisor) supervisors.add(r.supervisor);
    });
    return Array.from(supervisors);
  }, [reports]);

  // Apply Filters
  const filteredReports = useMemo(() => {
    return reports.filter(r => {
      // 1. Search Query
      const q = searchQuery.toLowerCase();
      const matchesSearch = 
        r.lot_id?.toLowerCase().includes(q) || 
        r.process_name?.toLowerCase().includes(q) ||
        r.First_Name?.toLowerCase().includes(q);

      // 2. Date Filter
      let matchesDate = true;
      if (dateFilter) {
        const rowDate = new Date(r.end_time).toISOString().split('T')[0];
        matchesDate = rowDate === dateFilter;
      }

      // 3. Worker Filter
      let matchesWorker = true;
      if (workerFilter) {
        matchesWorker = r.worker_id === workerFilter;
      }

      // 4. Supervisor Filter
      let matchesSupervisor = true;
      if (supervisorFilter) {
        matchesSupervisor = r.supervisor === supervisorFilter;
      }

      return matchesSearch && matchesDate && matchesWorker && matchesSupervisor;
    });
  }, [reports, searchQuery, dateFilter, workerFilter, supervisorFilter]);

  // Pagination Logic
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);
  const currentData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredReports.slice(start, start + itemsPerPage);
  }, [filteredReports, currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Excel / CSV Download
  const downloadCSV = () => {
    if (filteredReports.length === 0) {
      Swal.fire('Info', 'No data to download.', 'info');
      return;
    }

    const headers = ["Worker ID", "Worker Name", "Lot ID", "Process", "Target Qty", "Completed Qty", "Start Time", "End Time", "Efficiency (%)"];
    const rows = filteredReports.map(r => [
      r.worker_id,
      `${r.First_Name} ${r.Last_Name}`.trim(),
      r.lot_id,
      r.process_name,
      r.target_qty,
      r.completed_qty,
      formatDate(r.start_time).replace(/,/g, ''),
      formatDate(r.end_time).replace(/,/g, ''),
      r.efficiency ? Number(r.efficiency).toFixed(1) : "0"
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map(e => e.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Production_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="d-flex dashboard">
      {/* ── SIDEBAR ── */}
      <div className={isSidebarVisible ? "sidebar-container shadow-sm" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1">
        {/* ── HEADER ── */}
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        {/* ── MAIN CONTENT AREA ── */}
        <main className="main-container p-0 pb-5" style={{ background: "#f8fafc", fontFamily: "'Inter', sans-serif", minHeight: "calc(100vh - 60px)" }}>
          
          {/* PAGE BANNER */}
          <div style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            padding: "40px 48px", color: "#fff",
            position: "relative",
            overflow: "hidden"
          }}>
            <div style={{ position: "absolute", top: -50, right: -50, width: 200, height: 200, background: "linear-gradient(135deg, rgba(16,185,129,0.3) 0%, rgba(59,130,246,0.2) 100%)", borderRadius: "50%", filter: "blur(40px)" }}></div>
            <div className="d-flex justify-content-between align-items-end">
              <div>
                <h1 style={{ margin: 0, fontSize: 32, fontWeight: 800, letterSpacing: "-0.5px", display: "flex", alignItems: "center" }}>
                  <div style={{ background: "rgba(16, 185, 129, 0.2)", padding: "10px", borderRadius: "12px", marginRight: "16px" }}>
                    <i className="bi bi-bar-chart-line-fill text-success" style={{ fontSize: "24px" }}></i> 
                  </div>
                  Daily Production Report
                </h1>
                <p style={{ margin: "12px 0 0 64px", opacity: 0.8, fontSize: 16, fontWeight: 500, maxWidth: "600px" }}>
                  Detailed overview of worker outputs, efficiency, and completed tasks for the day.
                </p>
              </div>
              <button 
                onClick={downloadCSV}
                className="btn btn-success px-4 py-2" 
                style={{ borderRadius: "10px", fontWeight: 600, boxShadow: "0 4px 6px -1px rgba(16, 185, 129, 0.2)" }}
              >
                <i className="bi bi-file-earmark-excel-fill me-2"></i> Export CSV
              </button>
            </div>
          </div>

          <div className="container-fluid mt-4" style={{ maxWidth: "1400px", padding: "0 40px" }}>
            
            {/* FILTERS SECTION */}
            <div className="card border-0 mb-4" style={{ borderRadius: "16px", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)", background: "#fff" }}>
              <div className="card-body p-4 d-flex gap-3 flex-wrap">
                <div style={{ flex: "1 1 200px" }}>
                  <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Search</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-muted"></i></span>
                    <input 
                      type="text" 
                      className="form-control border-start-0 ps-0" 
                      placeholder="Lot ID, Process..." 
                      value={searchQuery}
                      onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                      style={{ boxShadow: "none" }}
                    />
                  </div>
                </div>

                {normalizeRole(user?.Auth) === 'superadmin' && (
                  <div style={{ flex: "1 1 150px" }}>
                    <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Supervisor</label>
                    <select 
                      className="form-select" 
                      value={supervisorFilter} 
                      onChange={e => { setSupervisorFilter(e.target.value); setCurrentPage(1); }}
                      style={{ boxShadow: "none" }}
                    >
                      <option value="">All</option>
                      {uniqueSupervisors.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div style={{ flex: "1 1 150px" }}>
                  <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Filter by Date</label>
                  <input 
                    type="date" 
                    className="form-control" 
                    value={dateFilter}
                    onChange={e => { setDateFilter(e.target.value); setCurrentPage(1); }}
                    style={{ boxShadow: "none" }}
                  />
                </div>

                <div style={{ flex: "1 1 200px" }}>
                  <label style={{ fontSize: "12px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", marginBottom: "6px", display: "block" }}>Filter by Worker</label>
                  <select 
                    className="form-select" 
                    value={workerFilter} 
                    onChange={e => { setWorkerFilter(e.target.value); setCurrentPage(1); }}
                    style={{ boxShadow: "none" }}
                  >
                    <option value="">All Workers</option>
                    {uniqueWorkers.map(w => (
                      <option key={w.id} value={w.id}>{w.name} ({w.id})</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center" style={{ padding: "100px 0" }}>
                <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}></div>
                <h5 className="mt-4 text-secondary" style={{ fontWeight: 600 }}>Loading report data...</h5>
              </div>
            ) : (
              <div className="card border-0" style={{ borderRadius: "20px", overflow: "hidden", background: "#fff", boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)" }}>
                <div className="card-header bg-white border-0" style={{ padding: "30px 30px 20px" }}>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h5 style={{ fontWeight: 800, color: "#0f172a", margin: 0, fontSize: "20px" }}>
                        <i className="bi bi-card-checklist text-primary me-2"></i> Daily Production Tasks
                      </h5>
                    </div>
                    <span className="badge bg-success-subtle text-success" style={{ padding: "8px 16px", borderRadius: "20px", fontWeight: 700 }}>
                      {filteredReports.length} Records Found
                    </span>
                  </div>
                </div>
                
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className="table align-middle mb-0" style={{ fontSize: "15px" }}>
                      <thead style={{ background: "#1e293b" }}>
                        <tr>
                          <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 30px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Worker Info</th>
                          <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Lot / Process</th>
                          <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Qty & Evidence</th>
                          <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 20px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Timing</th>
                          <th style={{ color: "#ffffff", fontWeight: 700, padding: "16px 30px", border: "none", textTransform: "uppercase", fontSize: "12px", letterSpacing: "0.5px" }}>Efficiency</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentData.length === 0 ? (
                          <tr><td colSpan="5" className="text-center text-muted" style={{ padding: "60px 0" }}>
                            <i className="bi bi-inbox text-slate-300" style={{ fontSize: "40px", color: "#cbd5e1" }}></i>
                            <div className="mt-3" style={{ fontWeight: 600 }}>No tasks match your filters.</div>
                          </td></tr>
                        ) : currentData.map(r => (
                          <tr key={r.task_id} style={{ transition: "background 0.2s" }} onMouseOver={e => e.currentTarget.style.background = "#f8fafc"} onMouseOut={e => e.currentTarget.style.background = "transparent"}>
                            <td style={{ padding: "20px 30px", borderBottom: "1px solid #e2e8f0" }}>
                              <div style={{ fontWeight: 700, color: "#1e293b", fontSize: "15px" }}>{r.First_Name} {r.Last_Name}</div>
                              <div style={{ color: "#64748b", fontWeight: 500, fontSize: "13px", marginTop: "4px" }}>ID: {r.worker_id}</div>
                              <div style={{ color: "#3b82f6", fontWeight: 700, fontSize: "12px", marginTop: "4px", textTransform: "uppercase" }}><i className="bi bi-person-badge"></i> Sup: {r.supervisor}</div>
                            </td>
                            <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0" }}>
                              <div style={{ fontWeight: 800, color: "#0f172a" }}>{r.lot_id}</div>
                              <div className="badge bg-slate-100 text-slate-600 mt-2" style={{ background: "#e2e8f0", color: "#334155", fontWeight: 600 }}>{r.process_name}</div>
                            </td>
                            <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0" }}>
                              <div style={{ fontWeight: 700, color: "#64748b", fontSize: "13px" }}>Target: {r.target_qty}</div>
                              {r.status === 'IN_PROGRESS' ? (
                                <div style={{ fontWeight: 800, color: "#f59e0b", fontSize: "15px", marginTop: "2px" }}>
                                  <i className="bi bi-hourglass-split"></i> Running
                                </div>
                              ) : (
                                <div style={{ fontWeight: 800, color: "#10b981", fontSize: "15px", marginTop: "2px" }}>
                                  Done: {r.completed_qty}
                                </div>
                              )}
                              {r.rejected_qty > 0 && <div style={{ fontWeight: 700, color: "#ef4444", fontSize: "13px", marginTop: "2px" }}>Rejected: {r.rejected_qty}</div>}
                              {r.hold_reason && <div style={{ color: "#475569", fontSize: "12px", marginTop: "4px" }}>Note: {r.hold_reason}</div>}
                              <div className="d-flex gap-2 mt-2">
                                {r.photo_url && (
                                  <a href={`${IMAGE_BASE_URL}${r.photo_url}`} target="_blank" rel="noreferrer">
                                    <img src={`${IMAGE_BASE_URL}${r.photo_url}`} alt="Completed" style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #22c55e' }} title="Completed Photo" />
                                  </a>
                                )}
                                {r.rejected_photo_url && (
                                  <a href={`${IMAGE_BASE_URL}${r.rejected_photo_url}`} target="_blank" rel="noreferrer">
                                    <img src={`${IMAGE_BASE_URL}${r.rejected_photo_url}`} alt="Rejected" style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px', border: '1px solid #ef4444' }} title="Rejected Photo" />
                                  </a>
                                )}
                              </div>
                            </td>
                            <td style={{ padding: "20px 20px", borderBottom: "1px solid #e2e8f0" }}>
                              <div style={{ fontSize: "13px", color: "#475569", fontWeight: 600 }}>In: {formatDate(r.start_time)}</div>
                              <div style={{ fontSize: "13px", color: "#475569", fontWeight: 600, marginTop: "4px" }}>Out: {formatDate(r.end_time)}</div>
                            </td>
                            <td style={{ padding: "20px 30px", borderBottom: "1px solid #e2e8f0" }}>
                              <span style={{ 
                                display: "inline-block", 
                                background: r.efficiency > 80 ? "#ecfdf5" : r.efficiency > 50 ? "#fef3c7" : "#fef2f2", 
                                color: r.efficiency > 80 ? "#059669" : r.efficiency > 50 ? "#d97706" : "#dc2626", 
                                padding: "6px 12px", borderRadius: "8px", fontWeight: 800, fontSize: "14px" 
                              }}>
                                {r.efficiency ? `${Number(r.efficiency).toFixed(1)}%` : "N/A"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* PAGINATION */}
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center p-4 border-top" style={{ borderColor: "#e2e8f0" }}>
                      <div style={{ fontSize: "14px", color: "#64748b", fontWeight: 600 }}>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredReports.length)} of {filteredReports.length} entries
                      </div>
                      <div className="d-flex gap-2">
                        <button 
                          className="btn btn-light border" 
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          style={{ fontWeight: 600, padding: "6px 16px" }}
                        >
                          Previous
                        </button>
                        <button 
                          className="btn btn-light border" 
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          style={{ fontWeight: 600, padding: "6px 16px" }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
